import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Form,
  Col,
  Row,
  InputGroup,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { ApiConfig } from "config/ApiConfig";
import ReactMarkdown from "react-markdown";
import { useAuth } from "context/AuthContext";

const LoginForm = ({ hasLabel }) => {
  const navigate = useNavigate();
  const { setZendeskSubdomain, checkAuthenticationStatus } = useAuth();

  // State
  const [formData, setFormData] = useState({
    remember: false,
    zendeskSubdomain: "",
    zendeskEmail: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [tncModalShow, setTNCModalShow] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showAuthButtons, setShowAuthButtons] = useState(false);
  const [authCheckLoading, setAuthCheckLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [popupTimeout, setPopupTimeout] = useState(null);

  // Check for auth errors on mount
  useEffect(() => {
    // Check if user came back from callback with auth error
    const urlParams = new URLSearchParams(window.location.search);
    const authError = urlParams.get("authError");

    if (authError) {
      toast.error("Authentication failed. Please try again.", {
        theme: "colored",
      });
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Monitor popup window closure
  useEffect(() => {
    if (popupOpen && window.zendeskAuthPopup) {
      const checkClosed = setInterval(() => {
        try {
          // Check if popup is closed or if we can't access it
          if (
            window.zendeskAuthPopup.closed ||
            !window.zendeskAuthPopup ||
            window.zendeskAuthPopup.closed === undefined
          ) {
            setPopupOpen(false);
            setShowAuthButtons(false); // Don't show buttons, auto-check instead
            clearInterval(checkClosed);
            if (popupTimeout) {
              clearTimeout(popupTimeout);
              setPopupTimeout(null);
            }
            window.zendeskAuthPopup = null;
            
            // Auto-run authentication check when popup is closed
            handleAutoAuthCheck();
          }
        } catch (error) {
          // If we can't access the popup, it's likely closed
          setPopupOpen(false);
          setShowAuthButtons(false); // Don't show buttons, auto-check instead
          clearInterval(checkClosed);
          if (popupTimeout) {
            clearTimeout(popupTimeout);
            setPopupTimeout(null);
          }
          window.zendeskAuthPopup = null;
          
          // Auto-run authentication check when popup is closed
          handleAutoAuthCheck();
        }
      }, 500); // Check more frequently

      // Also listen for window focus events to detect popup closure
      const handleFocus = () => {
        setTimeout(() => {
          try {
            if (window.zendeskAuthPopup && window.zendeskAuthPopup.closed) {
              setPopupOpen(false);
              setShowAuthButtons(false); // Don't show buttons, auto-check instead
              clearInterval(checkClosed);
              if (popupTimeout) {
                clearTimeout(popupTimeout);
                setPopupTimeout(null);
              }
              window.zendeskAuthPopup = null;
              
              // Auto-run authentication check when popup is closed
              handleAutoAuthCheck();
            }
          } catch (error) {
            setPopupOpen(false);
            setShowAuthButtons(false); // Don't show buttons, auto-check instead
            clearInterval(checkClosed);
            if (popupTimeout) {
              clearTimeout(popupTimeout);
              setPopupTimeout(null);
            }
            window.zendeskAuthPopup = null;
            
            // Auto-run authentication check when popup is closed
            handleAutoAuthCheck();
          }
        }, 100);
      };

      window.addEventListener("focus", handleFocus);

      // Also listen for visibility changes
      const handleVisibilityChange = () => {
        if (document.visibilityState === "visible") {
          setTimeout(() => {
            try {
              if (window.zendeskAuthPopup && window.zendeskAuthPopup.closed) {
                setPopupOpen(false);
                setShowAuthButtons(false); // Don't show buttons, auto-check instead
                clearInterval(checkClosed);
                if (popupTimeout) {
                  clearTimeout(popupTimeout);
                  setPopupTimeout(null);
                }
                window.zendeskAuthPopup = null;
                
                // Auto-run authentication check when popup is closed
                handleAutoAuthCheck();
              }
            } catch (error) {
              setPopupOpen(false);
              setShowAuthButtons(false); // Don't show buttons, auto-check instead
              clearInterval(checkClosed);
              if (popupTimeout) {
                clearTimeout(popupTimeout);
                setPopupTimeout(null);
              }
              window.zendeskAuthPopup = null;
              
              // Auto-run authentication check when popup is closed
              handleAutoAuthCheck();
            }
          }, 100);
        }
      };

      document.addEventListener("visibilitychange", handleVisibilityChange);

      // Cleanup interval and event listeners on component unmount
      return () => {
        clearInterval(checkClosed);
        window.removeEventListener("focus", handleFocus);
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }
  }, [popupOpen]);

  const dataPolicyContent = `
1. **INTRODUCTION**
    1. These Terms and Conditions ("**Terms**") govern the use of the Pytron Inc. ("**Pytron**", "**We**", "**Our**", "**Us**") owned AI-based software application, **TopCx.ai** ("**App**") by the client including its authorized employees and contractors ("**Client**", "You", "**Your**").
    2. By accessing or using the App, You agree to be bound by these Terms. If You do not agree to these Terms, You may not access or use the App.
    3. These Terms shall be effective upon Your registration on Our App and acceptance of the Terms hereof. Continued usage of Our App will be considered to be Your deemed and continued acceptance of these Terms.
    4. The aim of these Terms is to provide clear guidelines for using the App. By utilising Our service(s), You acknowledge that You have reviewed, comprehended, and consented to be bound by these Terms. If You disagree with these Terms, then You must cease further usage of this App.
    
These Terms were last updated on 15th May 2024
`;

  // Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Handle Zendesk authentication
    if (formData.zendeskSubdomain && formData.zendeskEmail) {
      await handleZendeskAuth();
    } else {
      toast.error("Please fill in both Zendesk subdomain and email address", {
        theme: "colored",
      });
    }
  };

  const handleZendeskAuth = async () => {
    // Validation
    if (!formData.zendeskSubdomain) {
      toast.error("Please enter your Zendesk subdomain", { theme: "colored" });
      return;
    }

    if (!/^[a-z0-9-]+$/.test(formData.zendeskSubdomain)) {
      toast.error(
        "Invalid subdomain. May only contain lowercase letters, numbers, and dashes (-)",
        { theme: "colored" }
      );
      return;
    }

    if (!formData.zendeskEmail) {
      toast.error("Please enter your email address", { theme: "colored" });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.zendeskEmail)) {
      toast.error("Please enter a valid email address", { theme: "colored" });
      return;
    }

    setIsLoading(true);

    try {
      const authData = {
        email: formData.zendeskEmail,
        subdomain: formData.zendeskSubdomain,
        crm_name: "zendesk",
        id: "18477716765212",
        role: "admin",
        source: "admin_screen",
        user_auth: true,
        ticketId: localStorage.getItem("zendesk_ticketId") || null,
      };

      const response = await axios.post(ApiConfig.login, authData, {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response.data.redirectUrl) {
        // Store subdomain in context for later use
        setZendeskSubdomain(formData.zendeskSubdomain);

        // Open Zendesk OAuth in popup window
        const popup = window.open(
          response.data.redirectUrl,
          "zendesk_auth",
          "width=600,height=700,scrollbars=yes,resizable=yes,top=100,left=100"
        );

        // Store popup reference for later use
        window.zendeskAuthPopup = popup;

        // Add event listener to popup to detect when it's closed
        if (popup) {
          popup.addEventListener("beforeunload", () => {
            setTimeout(() => {
              setPopupOpen(false);
              setShowAuthButtons(false); // Don't show buttons, auto-check instead
              if (popupTimeout) {
                clearTimeout(popupTimeout);
                setPopupTimeout(null);
              }
              window.zendeskAuthPopup = null;
              
              // Auto-run authentication check when popup is closed
              handleAutoAuthCheck();
            }, 100);
          });
        }

        // Set popup as open and hide main content
        setPopupOpen(true);
        setShowAuthButtons(false);

        // Set a fallback timeout to auto-check auth after 30 seconds
        const timeout = setTimeout(() => {
          setPopupOpen(false);
          setShowAuthButtons(false); // Don't show buttons, auto-check instead
          window.zendeskAuthPopup = null;
          
          // Auto-run authentication check when timeout is reached
          handleAutoAuthCheck();
        }, 30000); // 30 seconds timeout

        setPopupTimeout(timeout);

        toast.info(
          "Zendesk OAuth opened in popup window. Complete the login and close the popup to automatically continue.",
          {
            theme: "colored",
            autoClose: 5000,
          }
        );
      } else {
        navigate("/dashboard"); // Direct navigation if no OAuth required
      }
    } catch (error) {
      console.error("Login API error:", error);
      toast.error(
        error.response?.data?.message || "An error occurred. Please try again.",
        {
          theme: "colored",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleAcceptTerms = () => {
    setTNCModalShow(false);
    navigate("/dashboard");
  };

  const handleDeclineTerms = () => {
    setTNCModalShow(false);
    navigate("/decline");
  };

  const handleCheckboxChange = (e) => {
    setAccepted(e.target.checked);
  };

  // Function to automatically check authentication when popup is closed
  const handleAutoAuthCheck = async () => {
    setAuthCheckLoading(true);

    try {
      const authResult = await checkAuthenticationStatus();

      if (authResult) {
        setShowAuthButtons(false);
        setPopupOpen(false);

        // Clear any existing timeout
        if (popupTimeout) {
          clearTimeout(popupTimeout);
          setPopupTimeout(null);
        }

        // Close the popup window if it's still open
        if (window.zendeskAuthPopup && !window.zendeskAuthPopup.closed) {
          window.zendeskAuthPopup.close();
          window.zendeskAuthPopup = null;
        }

        toast.success(
          "Authentication successful! Redirecting to dashboard...",
          { theme: "colored" }
        );

        // Redirect directly to dashboard instead of showing T&C modal
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        // If auth not completed, show the manual check button as fallback
        setShowAuthButtons(true);
        toast.info(
          "Authentication not completed yet. Please complete the Zendesk login and try again.",
          { theme: "colored" }
        );
      }
    } catch (error) {
      console.error("Auto auth check error:", error);
      // If error occurs, show the manual check button as fallback
      setShowAuthButtons(true);
      toast.error("Error checking authentication status. Please try again.", {
        theme: "colored",
      });
    } finally {
      setAuthCheckLoading(false);
    }
  };

  // Function to manually check if authentication is completed (fallback)
  const handleCheckAuthCompleted = async () => {
    setAuthCheckLoading(true);

    try {
      const authResult = await checkAuthenticationStatus();

      if (authResult) {
        setShowAuthButtons(false);
        setPopupOpen(false);

        // Clear any existing timeout
        if (popupTimeout) {
          clearTimeout(popupTimeout);
          setPopupTimeout(null);
        }

        // Close the popup window if it's still open
        if (window.zendeskAuthPopup && !window.zendeskAuthPopup.closed) {
          window.zendeskAuthPopup.close();
          window.zendeskAuthPopup = null;
        }

        toast.success(
          "Authentication successful! Redirecting to dashboard...",
          { theme: "colored" }
        );

        // Redirect directly to dashboard instead of showing T&C modal
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        toast.error(
          "Authentication not completed yet. Please complete the Zendesk login first.",
          { theme: "colored" }
        );
      }
    } catch (error) {
      console.error("Manual auth check error:", error);
      toast.error("Error checking authentication status. Please try again.", {
        theme: "colored",
      });
    } finally {
      setAuthCheckLoading(false);
    }
  };


  return (
    <>
      {popupOpen ? (
        <div className="text-center">
          <div className="mb-4">
            <div className="display-4 mb-3">🔐</div>
            <h4 className="mb-2">Zendesk Authentication in Progress</h4>
            <p className="text-muted">
              Please complete the Zendesk login in the popup window. This page
              will be available once you close the popup.
            </p>
            <div className="mt-4">
              <Spinner
                animation="border"
                role="status"
                className="text-primary"
              >
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          </div>
        </div>
      ) : !showAuthButtons ? (
        <Form onSubmit={handleSubmit}>
          {/* Zendesk Fields */}
          <Form.Group className="mb-3">
            {hasLabel && <Form.Label>Zendesk Subdomain</Form.Label>}
            <InputGroup>
              <Form.Control
                placeholder={!hasLabel ? "your-company-url" : ""}
                value={formData.zendeskSubdomain}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase();
                  if (/^[a-z0-9-]*$/.test(value)) {
                    setFormData({
                      ...formData,
                      zendeskSubdomain: value,
                    });
                  }
                }}
                type="text"
                disabled={isLoading}
              />
              <InputGroup.Text>.zendesk.com</InputGroup.Text>
            </InputGroup>
            <Form.Text className="text-muted">
              Enter the first part of your Zendesk URL (e.g., "company" in
              company.zendesk.com)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            {hasLabel && <Form.Label>Email Address</Form.Label>}
            <Form.Control
              placeholder={!hasLabel ? "your@email.com" : ""}
              value={formData.zendeskEmail}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  zendeskEmail: e.target.value,
                })
              }
              type="email"
              disabled={isLoading}
            />
            <Form.Text className="text-muted">
              We'll use this email to associate with your Zendesk account
            </Form.Text>
          </Form.Group>

          <Row className="justify-content-between align-items-center">
            <Col xs="auto">
              <Form.Check type="checkbox" id="rememberMe" className="mb-0">
                <Form.Check.Input
                  type="checkbox"
                  name="remember"
                  checked={formData.remember}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      remember: e.target.checked,
                    })
                  }
                />
                <Form.Check.Label className="mb-0 text-700">
                  Remember me
                </Form.Check.Label>
              </Form.Check>
            </Col>
          </Row>

          <Form.Group>
            <Button
              type="submit"
              color="primary"
              className="mt-3 w-100"
              disabled={
                isLoading ||
                !formData.zendeskSubdomain ||
                !formData.zendeskEmail
              }
            >
              {isLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">Connecting...</span>
                </>
              ) : (
                "Connect Zendesk Account"
              )}
            </Button>
          </Form.Group>
        </Form>
      ) : (
        <div className="text-center">
          <div className="mb-4">
            <div className="display-4 mb-3">🔐</div>
            <h4 className="mb-2">Zendesk OAuth Opened</h4>
            <p className="text-muted">
              A popup window has been opened for Zendesk authentication. Please
              complete the login process there.
            </p>
          </div>
        </div>
      )}

      {/* Manual Authentication Buttons - shown when popup is open */}
      {showAuthButtons && (
        <div 
          className="mt-4 p-4 rounded-3 shadow-sm border-0"
          style={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <div className="text-center mb-4">
            <div className="mb-3">
              <div 
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-2"
                style={{
                  width: '60px',
                  height: '60px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                }}
              >
                <i className="fas fa-shield-alt text-white" style={{ fontSize: '1.5rem' }}></i>
              </div>
            </div>
            <h5 className="mb-2 fw-bold" style={{ color: '#2c3e50' }}>
              Authentication Complete
            </h5>
            <p className="text-muted mb-0">
              Your Zendesk account has been successfully connected. You can now access your dashboard.
            </p>
          </div>

          <div className="d-flex justify-content-center">
            <Button
              onClick={handleCheckAuthCompleted}
              disabled={authCheckLoading}
              className="px-5 py-3 fw-bold rounded-pill shadow-lg border-0"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontSize: '1.1rem',
                minWidth: '200px',
                transition: 'all 0.3s ease',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                if (!authCheckLoading) {
                  e.target.style.transform = 'translateY(-3px)';
                  e.target.style.boxShadow = '0 12px 35px rgba(102, 126, 234, 0.4)';
                  e.target.style.background = 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)';
                }
              }}
              onMouseLeave={(e) => {
                if (!authCheckLoading) {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.3)';
                  e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                }
              }}
            >
              {authCheckLoading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                    style={{ color: 'white' }}
                  />
                  <span>Accessing Dashboard...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-tachometer-alt me-2"></i>
                  Go to Dashboard
                </>
              )}
            </Button>
          </div>

          <div className="text-center mt-4">
            <div 
              className="d-inline-flex align-items-center px-3 py-2 rounded-pill"
              style={{
                background: 'rgba(102, 126, 234, 0.1)',
                border: '1px solid rgba(102, 126, 234, 0.2)'
              }}
            >
              <i className="fas fa-lightbulb me-2" style={{ color: '#667eea' }}></i>
              <small className="text-muted fw-medium">
                Ready to access your analytics dashboard
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Terms and Conditions Modal */}
      <Modal
        show={tncModalShow}
        onHide={() => setTNCModalShow(false)}
        backdrop="static"
        keyboard={false}
        size="xl"
        centered
      >
        <Modal.Body>
          <Modal.Title className="text-center mb-1">
            Terms and conditions
          </Modal.Title>

          <div
            className="p-4"
            style={{ maxHeight: "calc(100vh - 250px)", overflowY: "auto" }}
          >
            <ReactMarkdown>{dataPolicyContent}</ReactMarkdown>
          </div>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <Form.Check
              type="checkbox"
              label="I accept the terms and conditions"
              checked={accepted}
              onChange={handleCheckboxChange}
            />
            <div>
              <Button
                className="me-2"
                variant="danger"
                onClick={handleDeclineTerms}
              >
                Decline
              </Button>
              <Button
                className="me-2"
                variant="primary"
                onClick={handleAcceptTerms}
                disabled={!accepted}
              >
                Accept
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

LoginForm.propTypes = {
  hasLabel: PropTypes.bool,
};

LoginForm.defaultProps = {
  hasLabel: false,
};

export default LoginForm;
